import { useState } from "react"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import Tabs from "@mui/material/Tabs"
import Tab from "@mui/material/Tab"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"
import MDTypography from "components/MDTypography"
import {
  useUpdateVendorProposalCommunicationHistoryMutation,
  useGetVendorProposalRevisionsQuery,
} from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { formatVND, formatDate, proposalLineKey } from "../../constants"

const formatMoney = (v) => (v == null ? "—" : formatVND(v))

function Communications({ proposal, canWrite, tagArgs }) {
  const saved = proposal.communicationHistory || ""
  const [draft, setDraft] = useState(saved)
  const [save, { isLoading }] = useUpdateVendorProposalCommunicationHistoryMutation()

  const submit = async () => {
    try {
      await save({ id: proposal.id, communicationHistory: draft.trim() || null, ...tagArgs }).unwrap()
      showToastSuccess("Đã lưu lịch sử trao đổi.")
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không thể lưu lịch sử trao đổi. Vui lòng thử lại."))
    }
  }

  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      <MDInput
        label="Lịch sử trao đổi với nhà cung cấp"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        multiline
        minRows={6}
        fullWidth
        disabled={!canWrite || isLoading}
      />
      {canWrite && (
        <MDBox textAlign="right">
          <MDButton size="small" variant="gradient" color="info" onClick={submit} disabled={isLoading || draft === saved}>
            {isLoading ? "Saving…" : "Save"}
          </MDButton>
        </MDBox>
      )}
    </MDBox>
  )
}

const changed = (a, b) => (a ?? null) !== (b ?? null)

function Revision({ revision, previous }) {
  const prevItems = {}
  ;(previous?.snapshot?.items || []).forEach((it) => {
    prevItems[proposalLineKey(it)] = it
  })
  const snap = revision.snapshot || {}
  const highlight = (isChanged) => (isChanged ? { bgcolor: "warning.light", px: 0.5, borderRadius: 1 } : undefined)

  return (
    <MDBox p={1.5} borderRadius="md" sx={{ border: "1px solid", borderColor: "grey.300" }}>
      <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
        <MDTypography variant="button" fontWeight="bold">
          Lần {revision.revisionNo}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {formatDate(revision.submittedAt)}
        </MDTypography>
      </MDBox>
      {(snap.items || []).map((it) => {
        const prev = prevItems[proposalLineKey(it)]
        const priceChanged = !!previous && (!prev || changed(it.proposedAmount, prev.proposedAmount))
        return (
          <MDTypography key={proposalLineKey(it)} variant="caption" color="text" display="block">
            {it.itemName}:{" "}
            <MDBox component="span" sx={highlight(priceChanged)}>
              {formatMoney(it.proposedAmount)}
            </MDBox>
            {priceChanged && prev ? ` (trước: ${formatMoney(prev.proposedAmount)})` : ""}
            {it.note ? (
              <MDBox component="span" sx={highlight(previous && changed(it.note, prev?.note))}>
                {` — ${it.note}`}
              </MDBox>
            ) : (
              ""
            )}
          </MDTypography>
        )
      })}
      {snap.deliveryFee != null && (
        <MDTypography variant="caption" color="text" display="block">
          Phí giao:{" "}
          <MDBox component="span" sx={highlight(previous && changed(snap.deliveryFee, previous.snapshot?.deliveryFee))}>
            {formatMoney(snap.deliveryFee)}
          </MDBox>
        </MDTypography>
      )}
      <MDTypography variant="button" fontWeight="medium" display="block" mt={0.5}>
        Tổng:{" "}
        <MDBox component="span" sx={highlight(previous && changed(revision.totalAmount, previous.totalAmount))}>
          {formatMoney(revision.totalAmount)}
        </MDBox>
        {previous && changed(revision.totalAmount, previous.totalAmount)
          ? ` (trước: ${formatMoney(previous.totalAmount)})`
          : ""}
      </MDTypography>
    </MDBox>
  )
}

function Revisions({ proposalId }) {
  const { data: revisions = [], isFetching } = useGetVendorProposalRevisionsQuery(proposalId, {
    refetchOnMountOrArgChange: true,
  })
  if (!revisions.length) {
    return (
      <MDTypography variant="caption" color="text">
        {isFetching ? "Đang tải…" : "Nhà cung cấp chưa gửi báo giá qua link."}
      </MDTypography>
    )
  }
  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      {revisions
        .map((r, i) => <Revision key={r.id} revision={r} previous={revisions[i - 1]} />)
        .reverse()}
    </MDBox>
  )
}

export default function ProposalHistoryDialog({ proposal, sourceType, sourceId, canWrite }) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState(0)
  const isPurchase = sourceType === "purchase"
  const tagArgs = {
    orderRequestId: isPurchase ? undefined : proposal.orderRequestId ?? sourceId,
    purchaseId: isPurchase ? sourceId : undefined,
  }

  return (
    <>
      <MDButton size="small" variant="text" color="info" onClick={() => setOpen(true)} sx={{ px: 0 }}>
        History
      </MDButton>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{proposal.supplierName || proposal.supplier?.name}</DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label="Communication history" />
            <Tab label="Quote history" />
          </Tabs>
          {open && tab === 0 && <Communications proposal={proposal} canWrite={canWrite} tagArgs={tagArgs} />}
          {open && tab === 1 && <Revisions proposalId={proposal.id} />}
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="secondary" onClick={() => setOpen(false)}>
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
